import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, HardHat, Hammer, Wrench, Zap } from "lucide-react";
import { Asterisk } from "@/components/Asterisk";
import { ContactForm } from "@/components/ContactForm";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import { PageHero } from "@/components/PageHero";
import { breadcrumbScript, SITE_URL } from "@/lib/seo";

const tradeTypes = [
  {
    title: "Builders",
    body: "For builders, I organise SEO around the projects you want to quote for, such as extensions, renovations or structural work. Service pages explain the scope you handle and connect to relevant project examples. Clear coverage and enquiry questions help homeowners decide whether your business is a suitable fit before they contact you.",
    Icon: Hammer,
    href: "/seo/trades/builders" as const,
  },
  {
    title: "Electricians",
    body: "Electrical searches range from an urgent fault to a planned rewire or commercial inspection. I separate those needs in the website structure and make qualifications, service coverage and contact routes easy to find. The aim is to attract enquiries for work you are equipped and available to carry out.",
    Icon: Zap,
    href: "/seo/trades/electricians" as const,
  },
  {
    title: "Plumbers",
    body: "Plumbing customers need different information for a leak, a boiler installation or a bathroom project. I plan service pages around those situations, with accurate coverage, relevant credentials and an appropriate enquiry route. That helps the website support the jobs you want more of and makes it easier for customers to explain what they need.",
    Icon: Wrench,
    href: "/seo/trades/plumbers" as const,
  },
  {
    title: "Contractors",
    body: "Contractors need to show capability before a prospective client or procurement team gets in touch. I structure service, sector and project pages around the disciplines you deliver, supported by verified experience and accreditations. The enquiry journey then helps establish the project location, stage and scope needed for a useful first discussion.",
    Icon: HardHat,
    href: "/seo/trades/contractors" as const,
  },
] as const;

const tradeLocations = [
  { label: "Barnsley trades SEO", location: "barnsley" },
  { label: "Doncaster trades SEO", location: "doncaster" },
  { label: "Huddersfield trades SEO", location: "huddersfield" },
  { label: "Leeds trades SEO", location: "leeds" },
  { label: "Rotherham trades SEO", location: "rotherham" },
  { label: "Sheffield trades SEO", location: "sheffield" },
  { label: "Wakefield trades SEO", location: "wakefield" },
] as const;

export const Route = createFileRoute("/seo/trades/")({
  head: () => ({
    meta: [
      { title: "SEO for Trades | Builders, Electricians & Plumbers" },
      {
        name: "description",
        content:
          "SEO for trades and construction businesses, including builders, electricians, plumbers, and contractors across Yorkshire and the UK.",
      },
      { property: "og:title", content: "SEO for Trades | Phil Carr" },
      {
        property: "og:description",
        content:
          "Lead-focused SEO for builders, electricians, plumbers, and contractors that need stronger local visibility and better quote enquiries.",
      },
      { property: "og:url", content: "https://www.phil-carr.co.uk/seo/trades" },
    ],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/seo/trades" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: `${SITE_URL}/` },
        { name: "SEO", url: `${SITE_URL}/seo` },
        { name: "SEO for Trades", url: `${SITE_URL}/seo/trades` },
      ]),
    ],
  }),
  component: TradesPage,
});

function TradesPage() {
  return (
    <div className="trades-page">
      <PageHero title="SEO for trades and construction businesses" />

      <article className="inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-center">
          <div className="col-lg-7">
            <p className="demo-section-label">Trades SEO</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              Get found for the jobs you actually want
            </h2>
          </div>
          <div className="col-lg-5">
            <p className="site-copy text-muted-foreground">
              Trades SEO needs to do more than improve rankings. It should make your service areas,
              job types, trust signals, and quote paths clear enough for local buyers to act.
            </p>
          </div>
        </div>

        <div className="mt-16 row g-4">
          {tradeTypes.map((trade) => (
            <div className="col-md-6" key={trade.title}>
              <Link
                to={trade.href}
                className="group flex h-full flex-col border border-border bg-background/70 text-foreground no-underline transition-colors hover:border-primary"
              >
                <div className="trade-icon-visual" aria-hidden="true">
                  <trade.Icon className="h-20 w-20 text-primary" strokeWidth={1.4} />
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="site-heading-md font-display font-light group-hover:text-primary">
                    {trade.title}
                  </h3>
                  <p className="site-copy mt-4 text-muted-foreground">{trade.body}</p>
                  <span className="demo-link-arrow-button group/cta mt-6 inline-flex">
                    Read more
                    <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </article>

      <section className="trades-includes border-t border-border bg-background">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6">
              <p className="demo-section-label">What's included</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                What trades SEO usually includes
              </h2>
            </div>
            <div className="col-lg-6">
              <ul className="bootstrap-list-row row list-none pl-0">
                {[
                  "Service pages built around real local search demand",
                  "Clear service-area coverage for nearby towns and villages",
                  "Review, proof, case-study, and trust signal improvements",
                  "Conversion paths for calls, quote forms, and urgent enquiries",
                ].map((item) => (
                  <li className="col-12 flex items-start gap-3" key={item}>
                    <Asterisk className="mt-1 h-4 w-4 shrink-0 text-accent" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="demo-link-arrow-button group mt-8 no-underline">
                Talk about trades SEO
                <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="trades-local-coverage border-t border-b border-border bg-background/95">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-center">
            <div className="col-lg-5">
              <p className="demo-section-label">Local coverage</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">Trades SEO by area</h2>
              <img
                src="/images/trades-yorkshire.jpg?v=2"
                alt="UK builder, electrician and plumber working on Yorkshire construction sites"
                title="UK builder, electrician and plumber working on Yorkshire construction sites"
                data-name="UK builder, electrician and plumber working on Yorkshire construction sites"
                width={1280}
                height={960}
                loading="lazy"
                className="mt-8 aspect-square w-full object-cover border border-border"
              />
            </div>
            <div className="col-lg-7">
              <div className="row g-3">
                {tradeLocations.map((item) => (
                  <div className="col-sm-6" key={item.location}>
                    <Link
                      to="/seo/$location/$topic"
                      params={{ location: item.location, topic: "trades" }}
                      className="flex h-full items-center gap-3 border border-border bg-card/70 p-4 text-foreground transition-colors hover:border-primary hover:text-primary"
                    >
                      <Asterisk className="h-4 w-4 shrink-0 text-accent" />
                      <span className="font-semibold">{item.label}</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedNews
        slugs={[
          "how-builders-can-get-more-quote-requests-from-google",
          "seo-mistakes-that-cost-builders-local-leads",
          "seo-for-electricians-emergency-versus-planned-search-intent",
        ]}
      />

      <CtaBand />
      <section className="inner-contact-section border-t border-border bg-background/95">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5">
              <p className="demo-section-label">Get in touch</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Start a <em>conversation</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                Share your trade, service areas, and the type of enquiries you want more of.
              </p>
            </div>
            <div className="col-lg-7">
              <ContactForm pageContext="SEO for trades" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
